import React, { useState, useEffect, useRef } from "react";
import './chat.css';
import { Mic, MicOff, Send, Trash2, Volume2, VolumeX } from 'lucide-react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';


const LANGUAGE_OPTIONS = [
    { code: 'hi-IN', label: 'Hindi (India)' },
    { code: 'en-IN', label: 'English (India)' },
    { code: 'en-US', label: 'English (United States)' },
    { code: 'en-GB', label: 'English (United Kingdom)' },
    { code: 'de-DE', label: 'German (Germany)' },
    { code: 'es-ES', label: 'Spanish (Spain)' },
    { code: 'es-US', label: 'Spanish (United States)' },
    { code: 'fr-FR', label: 'French (France)' },
    { code: 'id-ID', label: 'Indonesian (Indonesia)' },
    { code: 'it-IT', label: 'Italian (Italy)' },
    { code: 'ja-JP', label: 'Japanese (Japan)' },
    { code: 'ko-KR', label: 'Korean (Korea)' },
    { code: 'nl-NL', label: 'Dutch (Netherlands)' },
    { code: 'pl-PL', label: 'Polish (Poland)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ru-RU', label: 'Russian (Russia)' },
    { code: 'zh-CN', label: 'Chinese (China Mainland)' },
    { code: 'zh-HK', label: 'Chinese (Hong Kong)' },
    { code: 'zh-TW', label: 'Chinese (Taiwan)' },
];

const AIAssistantPage = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [language, setLanguage] = useState('hi-IN'); // Default Hindi
    const [aiMuted, setAiMuted] = useState(false);
    const messagesEndRef = useRef(null);

    const {
        transcript,
        resetTranscript,
        listening: isListening,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    // Load messages from localStorage on mount
    useEffect(() => {
        const savedMessages = localStorage.getItem('mindwell-chat-messages');
        const savedLanguage = localStorage.getItem('mindwell-chat-language');
        const savedMuted = localStorage.getItem('mindwell-ai-muted');
        
        if (savedMessages) {
            try {
                setMessages(JSON.parse(savedMessages));
            } catch (e) {
                console.error('Error loading messages:', e);
            }
        }
        if (savedLanguage) {
            setLanguage(savedLanguage);
        }
        if (savedMuted) {
            setAiMuted(savedMuted === 'true');
        }
    }, []);

    // Save messages to localStorage whenever they change
    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem('mindwell-chat-messages', JSON.stringify(messages));
        }
    }, [messages]);

    // Save language preference
    useEffect(() => {
        localStorage.setItem('mindwell-chat-language', language);
    }, [language]);

    // Save AI muted preference
    useEffect(() => {
        localStorage.setItem('mindwell-ai-muted', aiMuted.toString());
    }, [aiMuted]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (transcript) {
            setInput(transcript);
        }
    }, [transcript]);

    const sendMessage = async () => {
        if (!input.trim()) return;

        const userMessage = { sender: "user", text: input };
        setMessages(prev => [...prev, userMessage]);

        // Prepare last 5 messages as history
        const history = [...messages, userMessage].slice(-5);

        try {
            const response = await fetch("http://localhost:8000/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text: input, history, language }),
            });

            const data = await response.json();
            const botMessage = {
                sender: "bot",
                text: typeof data.response === "string" ? data.response : JSON.stringify(data.response),
            };

            setMessages(prev => {
                const newMessages = [...prev, botMessage];
                speak(botMessage.text, language); // Speak the bot's reply
                return newMessages;
            });
        } catch (error) {
            console.error("Chatbot Error:", error);
        }

        setInput("");
        resetTranscript();
    };

    const handleMicClick = () => {
        if (!browserSupportsSpeechRecognition) {
            alert("Speech recognition not supported in your browser.");
            return;
        }

        if (isListening) {
            SpeechRecognition.stopListening();
        } else {
            SpeechRecognition.startListening({ continuous: false, language });
        }
    };

    const speak = (text, lang = language) => {
        if (aiMuted || !('speechSynthesis' in window)) return;
        
        window.speechSynthesis.cancel();
        const utterance = new window.SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        // Try to pick a matching voice for the selected language
        const voices = window.speechSynthesis.getVoices();
        // Try exact match first
        let matchVoice = voices.find(v => v.lang === lang);
        // If not found, try partial match (e.g., 'hi' for 'hi-IN')
        if (!matchVoice) {
            const baseLang = lang.split('-')[0];
            matchVoice = voices.find(v => v.lang.startsWith(baseLang));
        }
        if (matchVoice) {
            utterance.voice = matchVoice;
        }
        window.speechSynthesis.speak(utterance);
    };

    const handleClearConversation = () => {
        if (window.confirm('Are you sure you want to clear the conversation?')) {
            setMessages([]);
            localStorage.removeItem('mindwell-chat-messages');
        }
    };

    const handleMuteToggle = () => {
        if (!aiMuted) {
            // If currently unmuted, stop any ongoing speech immediately
            window.speechSynthesis.cancel();
        }
        setAiMuted(!aiMuted);
    };

    return (
      <>
        <div className="ai-chat-fullscreen">
            <div className="chat-settings-header">
                <div className="settings-left">
                    <select 
                        value={language} 
                        onChange={(e) => setLanguage(e.target.value)}
                        className="language-selector"
                    >
                        {LANGUAGE_OPTIONS.map(lang => (
                            <option key={lang.code} value={lang.code}>
                                {lang.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="settings-right">
                    <button 
                        onClick={handleMuteToggle}
                        className={`mute-btn ${aiMuted ? 'muted' : ''}`}
                        title={aiMuted ? 'Unmute AI Voice' : 'Mute AI Voice'}
                    >
                        {aiMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                        <span>{aiMuted ? 'Unmute' : 'Mute'}</span>
                    </button>
                    <button 
                        onClick={handleClearConversation}
                        className="clear-btn"
                        title="Clear Conversation"
                    >
                        <Trash2 size={18} />
                        <span>Clear</span>
                    </button>
                </div>
            </div>
            <div className="ai-chat-messages">
                {messages.length === 0 && (
                    <div className="welcome-message">
                        <div className="ai-chat-message bot">
                            <div className="bot-avatar">AI</div>
                            <div className="message-content">
                                Hi there! I'm MindWell AI, your mental wellness companion. I'm here to listen, support, and help you navigate whatever you're going through. How are you feeling today?
                            </div>
                        </div>
                    </div>
                )}
                {messages.map((msg, index) => (
                    <div key={index} className={`message-wrapper ${msg.sender}`}>
                        {msg.sender === 'bot' && <div className="bot-avatar">AI</div>}
                        <div className={`ai-chat-message ${msg.sender}`}>
                            <div className="message-content">
                                {msg.text.split('\n').map((line, i) => (
                                    <div key={i}>{line}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            <div className="ai-chat-input">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            sendMessage();
                        }
                    }}
                    placeholder="Type your message..."
                />
                <button onClick={handleMicClick} title="Voice input" className="mic-button">
                    {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                </button>
                <button onClick={sendMessage} className="send-button">
                    <Send size={20} />
                </button>
            </div>
        </div>
      </>
    );
};

export default AIAssistantPage;
